import { Typography } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import { a4PrintConfig } from "../../../util/commonConfig";
import { printFromNode, warmPrintFrame } from "../../../util/fastPrint";
import ContentToPrint from "../../CommonScreens/ContentToPrint/ContentToPrint";
import { getCertificatePreviewConfig } from "./certificateConstants";

const parseLocalUser = () => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const CertificatePreviewModal = ({
  open = false,
  cert = null,
  onClose = () => {},
}) => {
  const contentToPrint = useRef(null);
  const clinicFromRedux = useSelector((state) => state?.clinic?.details);

  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const clinicFromMandate = useMemo(() => {
    if (!mandateDataRaw) return {};
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return reqData?.clinic ?? {};
    } catch {
      return {};
    }
  }, [mandateDataRaw]);

  const profile = useMemo(() => parseLocalUser(), []);

  const clinicDet = useMemo(() => {
    const mandate = clinicFromMandate ?? {};
    const redux =
      clinicFromRedux && typeof clinicFromRedux === "object"
        ? clinicFromRedux
        : {};
    const merged = {
      ...redux,
      ...mandate,
      image: mandate?.image || redux?.image || null,
      footer: mandate?.footer || redux?.footer || null,
      name: mandate?.name || redux?.name || "",
      address: mandate?.address || redux?.address || "",
      taxId: mandate?.taxId || redux?.taxId || "",
      mobile: mandate?.mobile || redux?.mobile || "",
    };

    const profileId = profile?.id ?? profile?.userId ?? null;
    const clinicUserId =
      merged?.userId ?? merged?.id ?? merged?.createdBy ?? null;
    const idsMatch =
      profileId != null &&
      clinicUserId != null &&
      String(profileId) === String(clinicUserId);

    // Use logo only when localStorage user id matches clinic id
    if (idsMatch) {
      merged.image = merged.image || profile?.image || null;
    } else {
      merged.image = null;
    }

    return merged;
  }, [clinicFromMandate, clinicFromRedux, profile]);

  const preview = useMemo(() => getCertificatePreviewConfig(cert), [cert]);

  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      const node = contentToPrint.current;
      if (node) warmPrintFrame(node, a4PrintConfig);
    };
    const frameId = requestAnimationFrame(warm);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [open, cert?.id, preview?.printTitle, clinicDet?.image]);

  const renderClinicPrintFooter = () => (
    <div className="prescription-print-footer-inner">
      <div className="mb20 flex-start prescription-print-footer-doctor-row" />
      {clinicDet?.footer?.length > 0 ? (
        <img src={clinicDet?.footer} alt="" />
      ) : null}
    </div>
  );

  const renderClinicPrintHeader = (title) => (
    <div className="flex-row-ali-cen mv20 cert-print-header-row">
      <div className="w30Per">
        <div>
          {clinicDet?.image ? (
            <img src={clinicDet?.image} className="img-h75" alt="" />
          ) : (
            <div className="flex-center h50img">
              <Typography className="font-bold fs30 white-color capitalize">
                {clinicDet?.name?.[0]}
              </Typography>
            </div>
          )}
        </div>
      </div>
      <div className="w40Per flex-center cert-print-header-title-wrap">
        <div className="font-bold fs20 blue-color cert-print-header-title">
          {title}
        </div>
      </div>
      <div className="w30Per flex1-end">
        <div className="prescription-doc-header-clinic">
          <div className="font-bold blue-color fs16">
            {clinicDet?.name ?? ""}
          </div>
          <div className="txt-regular fs12 blue-color">
            {clinicDet?.address ?? ""}
          </div>
          {clinicDet?.taxId?.length > 0 ? (
            <div className="flex-row-ali-cen mt5">
              <div className="font-bold fs12 blue-color">TAX ID: </div>
              <div className="txt-regular fs12 blue-color ml3">
                {clinicDet?.taxId ?? ""}
              </div>
            </div>
          ) : null}
          {clinicDet?.mobile?.length > 0 ? (
            <div className="flex-row-ali-cen mt5">
              <div className="font-bold fs12 blue-color">Contact: </div>
              <div className="txt-regular fs12 blue-color ml3">
                {clinicDet?.mobile ?? ""}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  const handlePrint = () => {
    printFromNode(contentToPrint.current, {
      title: preview?.certNo
        ? `Certificate ${preview.certNo}`
        : preview?.printTitle || "Certificate",
      pageStyle: a4PrintConfig,
    });
  };

  const patient = preview?.patient ?? {};

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      header=""
      headerCenter
      modal
      headerBold
      modalWidth={80}
    >
      <div className="scroll-80vh">
        {preview ? (
          <ContentToPrint
            ref={contentToPrint}
            footer={renderClinicPrintFooter()}
            header={renderClinicPrintHeader(preview.printTitle)}
          >
            <tr>
              <td className="prescription-print-tbody-cell">
                <div className="prescription-print-body-inner">
                  <div>
                    <div className="vaccine-cert-no">
                      Certificate No. {preview.certNo}
                    </div>
                    <div className="vaccine-cert-doc">
                      <div className="vaccine-cert-section-title">PATIENT</div>
                      <div className="vaccine-cert-patient-grid">
                        <div className="vaccine-cert-field">
                          <span className="vaccine-cert-field-label">
                            Name:
                          </span>
                          <span className="vaccine-cert-field-value capitalize">
                            {patient.name}
                          </span>
                        </div>
                        <div className="vaccine-cert-field">
                          <span className="vaccine-cert-field-label">
                            Owner:
                          </span>
                          <span className="vaccine-cert-field-value capitalize">
                            {patient.owner}
                          </span>
                        </div>
                        <div className="vaccine-cert-field">
                          <span className="vaccine-cert-field-label">
                            Species / Breed:
                          </span>
                          <span className="vaccine-cert-field-value capitalize">
                            {patient.speciesBreed}
                          </span>
                        </div>
                        <div className="vaccine-cert-field">
                          <span className="vaccine-cert-field-label">
                            Owner Address:
                          </span>
                          <span className="vaccine-cert-field-value capitalize">
                            {patient.ownerAddress}
                          </span>
                        </div>
                        <div className="vaccine-cert-field">
                          <span className="vaccine-cert-field-label">
                            Sex / Age:
                          </span>
                          <span className="vaccine-cert-field-value capitalize">
                            {patient.sexAge}
                          </span>
                        </div>
                        <div className="vaccine-cert-field">
                          <span className="vaccine-cert-field-label">
                            Microchip No.:
                          </span>
                          <span className="vaccine-cert-field-value">
                            {patient.microchipNo}
                          </span>
                        </div>
                        <div className="vaccine-cert-field">
                          <span className="vaccine-cert-field-label">
                            Weight / Colour:
                          </span>
                          <span className="vaccine-cert-field-value capitalize">
                            {patient.weightColour}
                          </span>
                        </div>
                        <div className="vaccine-cert-field">
                          <span className="vaccine-cert-field-label">
                            Date of Birth:
                          </span>
                          <span className="vaccine-cert-field-value">
                            {patient.dob}
                          </span>
                        </div>
                      </div>

                      {preview.detailHeaders?.length ? (
                        <>
                          <div className="vaccine-cert-section-title">
                            DETAILS
                          </div>
                          <table className="vaccine-cert-table">
                            <thead>
                              <tr>
                                {preview.detailHeaders.map((h) => (
                                  <th key={h}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                {(preview.detailRow ?? []).map((cell, idx) => (
                                  <td key={`${preview.certNo}-${idx}`}>
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            </tbody>
                          </table>
                        </>
                      ) : null}

                      <div className="vaccine-cert-statement">
                        {preview?.note ||
                          cert?.note ||
                          cert?.footerNote ||
                          preview?.footerNote ||
                          preview?.statement ||
                          ""}
                      </div>

                      <div className="vaccine-cert-sign-wrap">
                        <div className="vaccine-cert-sign-row">
                          <div className="vaccine-cert-sign-block vaccine-cert-sign-left">
                            <div className="vaccine-cert-sign-line" />
                            <div className="vaccine-cert-sign-name">
                              {preview.vetName}
                            </div>
                            <div className="vaccine-cert-sign-sub">
                              Veterinary License No. {preview.vetLicense}
                            </div>
                          </div>
                          <div className="vaccine-cert-sign-block vaccine-cert-sign-right">
                            <div className="vaccine-cert-sign-line" />
                            <div className="vaccine-cert-sign-name">
                              {preview.issuedDate}
                            </div>
                            <div className="vaccine-cert-sign-sub">
                              Date Issued
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </ContentToPrint>
        ) : null}
        <div className="flex-center mv20">
          <div>
            <CustomButton
              text="Print / Download PDF"
              onClick={handlePrint}
              smallBtn
            />
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default CertificatePreviewModal;
